/**
 * RechnungskontrollePage — Administration › Rechnungskontrolle (Story UI-U6 AC6).
 *
 * K0–K3 band editor (three bounds b1=Rundungstoleranz / b2=t1 / b3=t2) with the
 * escalation ladder Neutral→Info→Warning→Danger (design.md §2 — green is NOT the
 * resting state); a Prozent (%)/Betrag (CHF) unit toggle mapping to the stored
 * `mode` (ONE mode per row — the toggle changes what will be SAVED, no parallel
 * value sets); the fixed "Pflichtregeln – immer K3" info section; per-Sparte
 * exception cards; and a Testmodus dry-run panel ("keine Mutation"). All
 * money/percent values stay STRINGS on the wire.
 *
 * The whole page is admin-gated (class-level tolerance_config.manage).
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { LineOfBusiness } from '@brokly/shared';
import { StatusDot, type StatusTone } from '../../../components/ui/StatusDot';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { useToleranceConfig } from '../hooks';
import { Button, LoadingState, ErrorState, TextInput } from '../components';
import type { ToleranceConfigEntryDto, ToleranceMode } from '../types';

const meta = ADMIN_PAGE_META.rechnungspruefung;

const LOB_LABELS: Record<LineOfBusiness, string> = {
  uvg: 'UVG (Unfall)',
  ktg: 'KTG (Krankentaggeld)',
  bu: 'BU (Berufsunfähigkeit)',
  leben: 'Leben',
  sach: 'Sach',
  haftpflicht: 'Haftpflicht',
  kranken: 'Kranken',
  other: 'Übrige',
};
const ALL_LOB = Object.keys(LOB_LABELS) as LineOfBusiness[];

const K_LEVELS: Array<{ k: string; tone: StatusTone; label: string }> = [
  { k: 'K0', tone: 'neutral', label: 'Rundung, sauber' },
  { k: 'K1', tone: 'info', label: 'innerhalb Toleranz' },
  { k: 'K2', tone: 'warning', label: 'signifikant' },
  { k: 'K3', tone: 'danger', label: 'Eskalation' },
];

function ModeToggle({
  mode,
  onChange,
}: {
  mode: ToleranceMode;
  onChange: (m: ToleranceMode) => void;
}) {
  return (
    <div className="inline-flex items-center gap-1 rounded-md bg-muted/60 p-0.5">
      {(['percent', 'absolute'] as const).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          aria-pressed={mode === m}
          className={
            mode === m
              ? 'rounded px-2.5 py-1 text-xs font-medium bg-background text-foreground shadow-xs'
              : 'rounded px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground'
          }
        >
          {m === 'percent' ? 'Prozent (%)' : 'Betrag (CHF)'}
        </button>
      ))}
    </div>
  );
}

function BandCard({ entry }: { entry: ToleranceConfigEntryDto }) {
  const qc = useQueryClient();
  const sparte = entry.line_of_business ?? 'default';
  const [mode, setMode] = useState<ToleranceMode>((entry.mode as ToleranceMode) ?? 'percent');
  const [b1, setB1] = useState(entry.rounding_tolerance);
  const [b2, setB2] = useState(entry.t1);
  const [b3, setB3] = useState(entry.t2);
  const [error, setError] = useState<string | null>(null);

  const unit = mode === 'percent' ? '%' : 'CHF';

  const save = useMutation({
    mutationFn: () =>
      apiClient.admin.setToleranceConfig(sparte, {
        mode,
        rounding_tolerance: b1,
        t1: b2,
        t2: b3,
      }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: qk.adminToleranceConfig() });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">
            {entry.line_of_business == null
              ? 'Standard (Mandant)'
              : (LOB_LABELS[entry.line_of_business as LineOfBusiness] ?? entry.line_of_business)}
          </span>
          {entry.is_default && (
            <span className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
              Standardwerte
            </span>
          )}
        </div>
        <ModeToggle mode={mode} onChange={setMode} />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {[
          { label: `Rundungstoleranz (b1, ${unit})`, val: b1, set: setB1 },
          { label: `t1 (b2, ${unit})`, val: b2, set: setB2 },
          { label: `t2 (b3, ${unit})`, val: b3, set: setB3 },
        ].map((f) => (
          <div key={f.label}>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">{f.label}</label>
            <TextInput
              inputMode="decimal"
              value={f.val}
              onChange={(e) => f.set(e.target.value)}
              className="tabular-nums"
            />
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        {K_LEVELS.map((lvl) => (
          <StatusDot key={lvl.k} tone={lvl.tone} label={`${lvl.k} · ${lvl.label}`} />
        ))}
      </div>

      {error != null && <div className="mt-2 text-sm text-status-danger">{error}</div>}

      <div className="mt-3 flex justify-end">
        <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
          Speichern
        </Button>
      </div>
    </div>
  );
}

function TestmodusPanel({ base }: { base: ToleranceConfigEntryDto }) {
  const [mode, setMode] = useState<ToleranceMode>((base.mode as ToleranceMode) ?? 'percent');
  const [b1, setB1] = useState(base.rounding_tolerance);
  const [b2, setB2] = useState(base.t1);
  const [b3, setB3] = useState(base.t2);
  const run = useMutation({
    mutationFn: () =>
      apiClient.admin.toleranceTestmodus({
        line_of_business: base.line_of_business,
        mode,
        rounding_tolerance: b1,
        t1: b2,
        t2: b3,
      }),
  });

  return (
    <div className="rounded-xl border border-dashed border-border bg-card/50 p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-sm font-semibold">Testmodus</div>
        <span className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
          keine Mutation
        </span>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Prüft die Kandidaten-Schwellen gegen historische Rechnungen (nur Vorschau).
      </p>
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="sm:col-span-1">
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Modus</label>
          <ModeToggle mode={mode} onChange={setMode} />
        </div>
        <TextInput value={b1} onChange={(e) => setB1(e.target.value)} className="tabular-nums" />
        <TextInput value={b2} onChange={(e) => setB2(e.target.value)} className="tabular-nums" />
        <TextInput value={b3} onChange={(e) => setB3(e.target.value)} className="tabular-nums" />
      </div>
      <div className="mt-3 flex items-center gap-3">
        <Button variant="outline" size="sm" disabled={run.isPending} onClick={() => run.mutate()}>
          Testlauf starten
        </Button>
        {run.data != null && (
          <span className="text-sm tabular-nums text-muted-foreground">
            {run.data.evaluated} geprüft · {run.data.changed.length} würden wechseln
          </span>
        )}
        {run.isError && <span className="text-sm text-status-danger">Testlauf fehlgeschlagen.</span>}
      </div>
    </div>
  );
}

export function RechnungskontrollePage() {
  const { data, isLoading, isError } = useToleranceConfig();
  const [extraSpartenRows, setExtraSpartenRows] = useState<LineOfBusiness[]>([]);

  const entries = useMemo(() => data ?? [], [data]);
  const defaultEntry = entries.find((e) => e.line_of_business == null);
  const spartenEntries = entries.filter((e) => e.line_of_business != null);

  const storedSparten = new Set(spartenEntries.map((e) => e.line_of_business));
  // A freshly-added row that has since been PERSISTED arrives via `spartenEntries`
  // on the next refetch — drop it from the local pending set so a saved exception
  // never renders twice (once as the stored card, once as a leftover blank card).
  const pendingExtraSparten = extraSpartenRows.filter((lob) => !storedSparten.has(lob));

  const usedSparten = new Set<LineOfBusiness | string | null>(storedSparten);
  pendingExtraSparten.forEach((s) => usedSparten.add(s));
  const addableSparten = ALL_LOB.filter((s) => !usedSparten.has(s));

  const blankFor = (lob: LineOfBusiness): ToleranceConfigEntryDto => ({
    line_of_business: lob,
    mode: defaultEntry?.mode ?? 'percent',
    rounding_tolerance: '0.00',
    t1: '0.00',
    t2: '0.00',
    valid_from: null,
    version: null,
    is_default: false,
  });

  return (
    <div>
      <AdminPageHeader title={meta.title} description={meta.description} />

      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState />
      ) : (
        <div className="space-y-6">
          <p className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
            Ein Moduswechsel (%/CHF) ändert die gespeicherte Einheit. Es werden keine zwei
            parallelen Wertsätze gehalten.
          </p>

          {defaultEntry != null && <BandCard key="default" entry={defaultEntry} />}

          {/* Pflichtregeln — immer K3 */}
          <div className="rounded-xl border border-status-danger-border bg-status-danger-bg/40 p-4">
            <div className="mb-1 flex items-center gap-2">
              <StatusDot tone="danger" label="Pflichtregeln: immer K3" />
            </div>
            <p className="text-xs text-muted-foreground">
              Bestimmte Abweichungen werden unabhängig von den Schwellen immer als K3 eskaliert
              (z. B. fehlende Zuordnung, Unterzahlung ausserhalb jeder Toleranz).
            </p>
          </div>

          {/* Per-Sparte exceptions */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm font-semibold">Ausnahmen pro Sparte</div>
              {addableSparten.length > 0 && (
                <select
                  aria-label="Sparte hinzufügen"
                  value=""
                  onChange={(e) => {
                    const lob = e.target.value as LineOfBusiness;
                    if (lob) setExtraSpartenRows((r) => [...r, lob]);
                  }}
                  className="h-8 rounded-md border border-border bg-background px-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">+ Sparte hinzufügen …</option>
                  {addableSparten.map((s) => (
                    <option key={s} value={s}>
                      {LOB_LABELS[s]}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {spartenEntries.length === 0 && pendingExtraSparten.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border bg-card/50 px-4 py-8 text-center text-sm text-muted-foreground">
                Keine Sparten-Ausnahmen, es gilt der Standard.
              </div>
            ) : (
              <div className="space-y-3">
                {spartenEntries.map((e) => (
                  <BandCard key={e.line_of_business} entry={e} />
                ))}
                {pendingExtraSparten.map((lob) => (
                  <div key={`new-${lob}`} className="space-y-1">
                    <div className="flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setExtraSpartenRows((r) => r.filter((x) => x !== lob))
                        }
                      >
                        Verwerfen
                      </Button>
                    </div>
                    <BandCard entry={blankFor(lob)} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {defaultEntry != null && <TestmodusPanel base={defaultEntry} />}
        </div>
      )}
    </div>
  );
}
